// All or portions of this file Copyright © 2019 Amazon Web Services, Inc. or its affiliates or licensors.
// All rights reserved. For full license terms and conditions, please see the LICENSE file at the root of
// this distribution (the "License") or go to https://content.sumerian.amazonaws.com/engine/latest/license.txt.
// All use of the software is governed by the License. All materials are made available on an "AS IS" BASIS, WITHOUT
// WARRANTIES OR CONDITIONS OF ANY KIND, express or implied. Do not remove or modify any license notices.
import { Accessor } from 'core/api/common/accessor';
import InternalEntity from 'core/entities/Entity';
import DialogueComponent from 'core/awspack/lex/DialogueComponent';

/**
 * @import {Entity} from 'core/api/common';
 * @import {Asset} from 'core/api/common';
 * @import {ValueForEntityInterface} from 'core/api/common/accessor/ValueInterface';
 */

/**
 * Provides access to the Lex bot attached to an entity. Pass this class to
 * {@link Entity#value} to create an object that can be used to access the
 * value.
 *
 * @extends {Accessor}
 * @extends {ValueForEntityInterface}
 */
export default class BotValue extends Accessor {

  /**
   * Create an instance of this accessor initialized for use with an entity.
   *
   * @param {Entity} entity - the entity being accessed.
   *
   * @return {BotValue} An object that can be used to access the
   * value.
   */
  static valueForEntity(entity) {
    return new BotValue(entity);
  }

  /**
   * Creates a new BotValue. Will create a new {@link DialogueComponent} on the provided entity if one doesn't exist.
   *
   * @private
   *
   * @param {Entity} entity The entity to query for the Lex bot.
   */
  constructor(entity) {
    super();

    if (!InternalEntity.forPublicEntity(entity).dialogueComponent) {
      InternalEntity.forPublicEntity(entity).setComponent(new DialogueComponent());
    }
    this._dialogueComponent = InternalEntity.forPublicEntity(entity).dialogueComponent;
  }

  /**
   * The name of the Lex bot attached to the entity.
   * @type {string}
   */
  get name() {
    return this._dialogueComponent.name;
  }
  
  set name(name) {
    this._dialogueComponent.name = name;
  }

  /**
   * The alias of the Lex bot attached to the entity.
   * @type {string}
   */
  get alias() {
    return this._dialogueComponent.alias;
  }

  set alias(alias) {
    this._dialogueComponent.alias = alias;
  }
}
